import React, { useMemo } from 'react';
import './style.scss';

export interface DonutChartParts {
  name: string;
  percentage: number;
  value: string;
}

export interface DonutChartSerie {
  name: string;
  percentage: number;
  offset: number;
  color: string;
  value: string;
  nr: number;
}

export interface DonutChartProps {
  parts: DonutChartParts[];
  showTotal?: boolean;
  getLabel?: (name: string) => string;
  thickness?: 's' | 'm' | 'l'; // Add this line
}

const COLORS = [
  "#0457B8", // Blue
  "#EF8A5B", // Orange
  "#21C99D", // Teal
  "#7CBF3F", // Green
  "#E85E76", // Pink
  "#0095A9", // Light Blue
  "#6A6CB5", // Purple
  "#F6B53C", // Yellow
];

const THICKNESS_MAP = {
  s: 2,
  m: 6,
  l: 8,
};

const getBackgroundColor = () =>
  window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches
    ? '#1c282e'
    : '#E7EAEC';

const DonutChart: React.FC<DonutChartProps> = ({
  parts,
  showTotal = false,
  getLabel,
  thickness = 'm', // Default to 'm'
}) => {
  const { chartParts, totalPercentage, totalCount } = useMemo(() => {
    let offset = 25;
    let totalPercentage = 0;
    let totalCount = 0;

    const chartParts: DonutChartSerie[] = parts.map((part, i) => {
      if (i > 0) {
        offset += part.percentage;
      }
      totalPercentage += part.percentage;
      if (showTotal) {
        totalCount += Number(part.value);
      }
      return {
        name: part.name,
        percentage: part.percentage,
        value: part.value,
        offset,
        color: COLORS[i % COLORS.length],
        nr: i,
      };
    });

    return { chartParts, totalPercentage, totalCount };
  }, [parts, showTotal]);

  const backgroundColor = getBackgroundColor();
  const strokeWidth = THICKNESS_MAP[thickness];

  return (
    <div className="donut-chart">
      <div className="chart">
        <svg width="100%" height="100%" viewBox="0 0 40 40" className="donut">
          {totalPercentage < 100 && (
            <circle
              className="donut-ring"
              cx="20"
              cy="20"
              r="15.91549430918954"
              fill="transparent"
              stroke={backgroundColor}
              strokeWidth={strokeWidth - 0.1} // Slightly thinner for the ring
            />
          )}
          {chartParts.map((row) => (
            <circle
              key={row.nr}
              className="donut-segment"
              cx="20"
              cy="20"
              r="15.91549430918954"
              fill="transparent"
              stroke={row.color}
              strokeWidth={strokeWidth}
              strokeDasharray={`${row.percentage},${100 - row.percentage}`}
              strokeDashoffset={row.offset}
            />
          ))}
        </svg>
        {showTotal && (
          <div className="total-count">
            <div style={{ fontSize: "2.5rem" }}>{totalCount}</div>
          </div>
        )}
      </div>
      <div className="chart-legend">
        {chartParts.map((row) => (
          <div className="legend-item" key={row.nr}>
            <div className="legend-color" style={{ backgroundColor: row.color }} />
            <div>
              {getLabel ? getLabel(row.name) : row.name} <strong>{row.value}</strong>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default DonutChart;
